import React, { useEffect } from 'react';


/**
 * A React Hook for holding a state value for a set amount of time.
 * @param initialValue - the starting value, and the value to which the state shall revert after a pre-defined delay
 * whenever set.
 * @param delay - the duration in milliseconds for which the state should remain set. Default: `3000`
 * @returns Array where first item is the state value, and the second item is a function that temporarily sets a new
 *          state value when called
 */
export const useTimedState = <T>(
  initialValue: T,
  delay: number = 3000,
): [T, (newValue: T) => void] => {
  const [value, setValue] = React.useState<T>(initialValue);

  useEffect(
    () => {
      if (value !== initialValue) {
        const handle = setTimeout(() => setValue(initialValue), delay);
        // Return a function to React that it can call when the component is unmounting to clean up this effect
        return () => clearTimeout(handle);
      }
    },
    [delay, initialValue, value],
  );

  return [value, setValue];
};
